<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from "@/store/AuthStore";
import { useSensorStore } from '@/store/SensorStore';
import { useSettingStore } from "@/store/SettingsStore";
import { useToast } from 'primevue/usetoast';

// PrimeVue Components
import Card from 'primevue/card';
import InputText from 'primevue/inputtext';
import Steps from 'primevue/steps';
import Toast from 'primevue/toast';
import Button from 'primevue/button';

// Initialization
const router = useRouter();
const authStore = useAuthStore();
const sensorStore = useSensorStore();
const settingStore = useSettingStore();
const toast = useToast();

// State
const activeStep = ref(0);
const isSubmitting = ref(false);

const items = ref([
  { label: 'User Info' },
  { label: 'Add Sensor' },
  { label: 'Finish' }
]);

const formData = ref({
  firstName: authStore.user?.first_name || '',
  lastName: authStore.user?.last_name || '',
  sensorName: 'My First Sensor',
  sensorLocation: 'Living Room',
});

// Methods
const nextStep = () => {
  if (activeStep.value < items.value.length - 1) {
    activeStep.value++;
  }
};

const submitAndFinish = async () => {
  isSubmitting.value = true;
  try {
    // Update user info
    await authStore.updateUserInfo({
      first_name: formData.value.firstName,
      last_name: formData.value.lastName
    });

    // Create the sensor
    await sensorStore.createSensor({
      name: formData.value.sensorName,
      location: formData.value.sensorLocation
    });

    // Hide the get started card for future visits
    await settingStore.updateSetting('getStartedShown', false);

    toast.add({ severity: 'success', summary: 'Setup Complete!', detail: 'Your account is ready.', life: 3000 });

    nextStep(); // Move to the final "Finish" step

  } catch (error) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'There was an issue completing the setup.', life: 3000 });
  } finally {
    isSubmitting.value = false;
  }
};

const exploreDashboard = () => {
  router.push('/dashboard');
};

onMounted(() => {
  // This is to ensure the fireworks component is mounted before we try to use it
  isMounted.value = true;
});
const isMounted = ref(false);

</script>

<template>
  <div class="py-4 container-fluid">
    <Toast />
<!--    <Fireworks ref="firework" :autostart="false" :options="fwOptions" style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; z-index: 9999;" />-->
    <div class="row justify-content-center">
      <div class="col-12 col-lg-10 col-xl-8">
        <Card class="get-started-card">
          <template #title>
            <div class="text-center">
              <h4 class="mb-2">Account Setup</h4>
              <p class="text-muted fw-normal">Just a few steps to get you up and running.</p>
            </div>
          </template>
          <template #content>
            <Steps :model="items" :activeStep="activeStep" class="mb-5" />

            <div v-if="activeStep === 0" class="step-panel">
              <h5 class="text-center mb-4">Tell us about yourself</h5>
              <div class="p-fluid">
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label for="firstName">First Name</label>
                    <InputText id="firstName" v-model="formData.firstName" />
                  </div>
                  <div class="col-md-6 mb-3">
                    <label for="lastName">Last Name</label>
                    <InputText id="lastName" v-model="formData.lastName" />
                  </div>
                </div>
              </div>
              <div class="text-end mt-4">
                <Button label="Next" icon="pi pi-arrow-right" iconPos="right" @click="nextStep" />
              </div>
            </div>

            <div v-if="activeStep === 1" class="step-panel">
              <h5 class="text-center mb-4">Register Your First Sensor</h5>
              <div class="p-fluid">
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label for="sensorName">Sensor Name</label>
                    <InputText id="sensorName" v-model="formData.sensorName" />
                  </div>
                  <div class="col-md-6 mb-3">
                    <label for="sensorLocation">Location</label>
                    <InputText id="sensorLocation" v-model="formData.sensorLocation" />
                  </div>
                </div>
              </div>
              <div class="d-flex justify-content-end mt-4">
                <Button label="Finish Setup" icon="pi pi-check" :loading="isSubmitting" @click="submitAndFinish" />
              </div>
            </div>

            <div v-if="activeStep === 2" class="step-panel text-center">
              <i class="pi pi-verified text-success" style="font-size: 4rem"></i>
              <h5 class="mt-3">Setup Complete!</h5>
              <p class="text-muted">Congratulations, your account is ready to go.</p>
              <Button label="Explore Dashboard" @click="exploreDashboard" class="mt-3" />
            </div>
          </template>
        </Card>
      </div>
    </div>
  </div>
</template>

<style scoped>
.get-started-card {
  box-shadow: 0 8px 25px -8px rgba(0,0,0,0.1);
}
.step-panel {
  padding: 2rem 0;
  min-height: 250px;
}
:deep(.p-steps .p-steps-item:before) {
  top: 50%;
}
</style>
